import type { AgentJob } from '@/apis/agent';
import type {
  DashboardFavoriteProposition,
  DashboardJobItem,
  DashboardMatchingFlow,
  DashboardRecentlyJob,
  DashboardSelectionProposition,
} from '@/apis/dashboard';
import type { DirectJob } from '@/apis/direct';
import type { CarouselItem, FavoriteCategory } from '@/components/shared/carousel/types';
import type { MessageSummaryCardProps } from '@/components/shared/message/types';
import type { AgentRecommendationItem } from '@/components/top/auth/agent-recommend/types';
import {
  isUserFavorited,
  toNumberOrUndefined,
  type FavoriteStateValue,
} from '@/composables/useTopAuthInteractions';
import type { AnnouncementProps } from '@/types/announcement';
import { jobsAgentDetailPath, jobsDirectDetailPath } from '@/utils/jobSearch/jobsPaths';
import { toPropositionStatus, toPropositionStatusFromStage } from '@/utils/messages/helpers';

type TopAuthMapperOptions = {
  getConsiderationState: (jobId: number) => { active: boolean } | undefined;
  getFavoriteState: (jobId: number) => FavoriteStateValue | undefined;
  onToggleConsideration: (jobId: number) => Promise<void>;
  onToggleFavorite: (jobId: number) => Promise<void>;
};

const getAgentStageTitle = (currentStageId: number): string => {
  switch (currentStageId) {
    case 1:
      return '選考終了';
    case 3:
      return 'エージェント確認中';
    case 34:
      return '内定';
    default:
      if (currentStageId >= 24 && currentStageId <= 33) {
        return '面接結果待ち';
      }
      return '選考中';
  }
};

const getAgentStageSnippet = (currentStageId: number): string => {
  switch (currentStageId) {
    case 1:
      return 'こちらの選考は終了しております。';
    case 3:
      return '現在エージェントが確認しております。確認でき次第、担当者からご連絡いたします。';
    case 34:
      return 'こちらの選考は内定となりました。おめでとうございます。';
    default:
      if (currentStageId >= 24 && currentStageId <= 33) {
        return 'こちらの選考は現在、面接の結果待ちです。エージェントからご連絡いたしますので、今しばらくお待ち下さい。';
      }
      return '選考が進行中です。';
  }
};

export const mapSelectionPropositionToMessageCard = (
  proposition: DashboardSelectionProposition
): MessageSummaryCardProps => {
  return {
    companyName: proposition.company_name ?? '',
    messageTitle: proposition.message_title ?? '選考のご案内',
    messageSnippet: proposition.job_title ?? 'メッセージをご確認ください。',
    messageId: proposition.id,
    isUnread: proposition.is_unread,
    detailPathPrefix: '/messages',
    replyStatus: proposition.is_unreplied ? 'unreplied' : 'replied',
    variant: proposition.is_unreplied ? 'tinted' : 'plain',
    propositionStatus: toPropositionStatus(proposition.action_type ?? null),
    isAgent: false,
  };
};

export const mapMatchingFlowToMessageCard = (
  m: DashboardMatchingFlow,
  isAgent: boolean
): MessageSummaryCardProps => {
  const rawSnippet =
    (m.occupation_descriptions?.length ? m.occupation_descriptions.join('、') : null) ??
    (m.industry_names?.length ? m.industry_names.join('、') : null) ??
    '選考が進行中です。';
  const messageTitle = isAgent
    ? getAgentStageTitle(m.current_stage_id)
    : m.job?.name ?? '選考のご案内';
  const messageSnippet = isAgent
    ? getAgentStageSnippet(m.current_stage_id)
    : rawSnippet.slice(0, 80);
  return {
    companyName: m.company?.name ?? '',
    messageTitle,
    messageSnippet,
    messageId: m.id,
    detailPathPrefix: '/messages',
    detailPathQuery: isAgent ? '?platform=agent' : undefined,
    variant: 'tinted',
    propositionStatus: toPropositionStatusFromStage(m.current_stage_id),
    isAgent,
  };
};

const favoriteStatusToCategory = (
  status: DashboardFavoriteProposition['favorite_status'] | DirectJob['favorite_status']
): FavoriteCategory | undefined => {
  if (status === 'company_favorite') return 'company';
  if (status === 'candidate_favorite') return 'candidate';
  if (status === 'matching_favorite' || status === 'matching') return 'matching';
  return undefined;
};

const formatFavoriteIncomeToManYen = (income: number | null): number | undefined => {
  if (!income) return undefined;
  return Math.round(income / 10000);
};

export const mapFavoriteToCarouselItem = (p: DashboardFavoriteProposition): CarouselItem => {
  const favoriteCategory = favoriteStatusToCategory(p.favorite_status);
  return {
    id: p.job_id,
    company: p.company_name,
    title: p.job_title,
    href: jobsDirectDetailPath(p.job_id),
    prefectureNames: [p.prefecture_name],
    occupationDescriptions: p.occupations,
    industryNames: p.industries,
    skills: p.skills,
    wageMin: formatFavoriteIncomeToManYen(p.min_income),
    wageMax: formatFavoriteIncomeToManYen(p.max_income),
    imageUrl: p.image_url ?? undefined,
    platform: 'direct',
    ...(favoriteCategory != null && { favoriteCategory }),
  };
};

export const mapDirectFavoriteToCarouselItem = (item: DirectJob): CarouselItem => {
  const favoriteCategory = favoriteStatusToCategory(item.favorite_status);
  return {
    id: item.job_id,
    company: item.company_name,
    title: item.job_title,
    href: jobsDirectDetailPath(item.job_id),
    prefectureNames: [item.prefecture_name ?? ''],
    occupationDescriptions: item.occupations,
    industryNames: item.industries,
    skills: item.skills,
    wageMin: formatFavoriteIncomeToManYen(item.min_income),
    wageMax: formatFavoriteIncomeToManYen(item.max_income),
    imageUrl: item.image_url ?? undefined,
    platform: 'direct',
    ...(favoriteCategory != null && { favoriteCategory }),
  };
};

export const mapAgentConsiderationToCarouselItem = (item: AgentJob): CarouselItem => ({
  id: item.job.id,
  company: item.company.name,
  title: item.job.name,
  href: jobsAgentDetailPath(item.job.id),
  prefectureNames: item.prefecture_names,
  occupationDescriptions: item.occupation_descriptions,
  industryNames: item.industry_names,
  skills: item.skill_names,
  wageMin: item.job.wage_min ?? undefined,
  wageMax: item.job.wage_max ?? undefined,
  imageUrl: item.image_url ?? undefined,
  platform: 'agent',
});

export const mapAnnouncementFromDashboard = (a: {
  id: number;
  title: string;
}): AnnouncementProps => ({
  id: a.id,
  title: a.title,
});

export const createTopAuthCardMappers = (options: TopAuthMapperOptions) => {
  const mapDashboardJobToRecommendItem = (item: DashboardJobItem): AgentRecommendationItem => {
    const jobId = toNumberOrUndefined(item.job.id);
    const recommendedJobId = item.id ?? item.job.id;
    return {
      id: recommendedJobId,
      comment: (item.job.job_recommendation && item.job.job_recommendation.trim()) || '',
      job: {
        companyName: item.company.name,
        jobTitle: item.job.name,
        prefectureNames: item.prefecture_names,
        occupationDescriptions: item.occupation_descriptions,
        industryNames: item.industry_names ?? undefined,
        skills: item.skill_names,
        wageMin: item.job.wage_min ?? undefined,
        wageMax: item.job.wage_max ?? undefined,
        imageUrl: item.job.image_url ?? undefined,
        href: `/recommended-jobs/${recommendedJobId}`,
        outlineButtonActive:
          jobId == null ? false : options.getConsiderationState(jobId)?.active ?? false,
        onBookmark: async () => {
          if (jobId == null) return;
          await options.onToggleConsideration(jobId);
        },
      },
    };
  };

  const mapMatchingFlowToCarouselItem = (m: DashboardMatchingFlow): CarouselItem => {
    const jobId = toNumberOrUndefined(m.job.id);
    return {
      id: m.job.id,
      company: m.company.name,
      title: m.job.name,
      href: jobsAgentDetailPath(m.job.id),
      prefectureNames: m.prefecture_names,
      occupationDescriptions: m.occupation_descriptions,
      industryNames: m.industry_names ?? undefined,
      skills: m.skill_names,
      wageMin: m.job.wage_min,
      wageMax: m.job.wage_max,
      platform: 'agent',
      outlineButtonActive:
        jobId == null ? false : options.getConsiderationState(jobId)?.active ?? false,
      onBookmark: async () => {
        if (jobId == null) return;
        await options.onToggleConsideration(jobId);
      },
    };
  };

  const mapDashboardJobToCarouselItem = (item: DashboardJobItem): CarouselItem => {
    const jobId = toNumberOrUndefined(item.job.id);
    const favoriteState = jobId == null ? undefined : options.getFavoriteState(jobId);
    const considerationState = jobId == null ? undefined : options.getConsiderationState(jobId);
    return {
      id: item.job.id,
      company: item.company.name,
      title: item.job.name,
      wageMin: item.job.wage_min ?? undefined,
      wageMax: item.job.wage_max ?? undefined,
      prefectureNames: item.prefecture_names,
      occupationDescriptions: item.occupation_descriptions,
      industryNames: item.industry_names ?? undefined,
      skills: item.skill_names,
      imageUrl: item.job.image_url ?? undefined,
      platform: item.is_direct ? 'direct' : 'agent',
      href: item.is_direct ? jobsDirectDetailPath(item.job.id) : jobsAgentDetailPath(item.job.id),
      outlineButtonActive: item.is_direct ? undefined : considerationState?.active ?? false,
      favoriteButtonActive: item.is_direct
        ? isUserFavorited(favoriteState?.favoriteStatus ?? null)
        : undefined,
      onBookmark:
        jobId == null
          ? undefined
          : async () => {
              if (item.is_direct) {
                await options.onToggleFavorite(jobId);
                return;
              }
              await options.onToggleConsideration(jobId);
            },
    };
  };

  const mapRecentlyJobToCarouselItem = (r: DashboardRecentlyJob): CarouselItem => {
    const jobId = toNumberOrUndefined(r.id);
    const favoriteState = jobId == null ? undefined : options.getFavoriteState(jobId);
    const considerationState = jobId == null ? undefined : options.getConsiderationState(jobId);
    return {
      id: r.id,
      company: r.company_name,
      title: r.title,
      href: r.platform === 'agent' ? jobsAgentDetailPath(r.id) : jobsDirectDetailPath(r.id),
      prefectureNames: [r.prefecture_name],
      occupationDescriptions: r.occupations,
      industryNames: r.industries,
      skills: r.skills,
      wageMin:
        r.platform === 'direct'
          ? formatFavoriteIncomeToManYen(r.min_income ?? null)
          : r.min_income ?? undefined,
      wageMax:
        r.platform === 'direct'
          ? formatFavoriteIncomeToManYen(r.max_income ?? null)
          : r.max_income ?? undefined,
      imageUrl: r.image_url ?? undefined,
      platform: r.platform,
      outlineButtonActive: r.platform === 'agent' ? considerationState?.active ?? false : undefined,
      favoriteButtonActive:
        r.platform === 'direct'
          ? isUserFavorited(favoriteState?.favoriteStatus ?? null)
          : undefined,
      onBookmark:
        jobId == null
          ? undefined
          : async () => {
              if (r.platform === 'direct') {
                await options.onToggleFavorite(jobId);
                return;
              }
              await options.onToggleConsideration(jobId);
            },
    };
  };

  return {
    mapDashboardJobToRecommendItem,
    mapMatchingFlowToCarouselItem,
    mapDashboardJobToCarouselItem,
    mapRecentlyJobToCarouselItem,
  };
};
